import Vue from 'vue'
import VueRouter from 'vue-router'
import FrontLayout from '../views/front/FrontLayout.vue'
import AdminLayout from '../views/admin/AdminLayout.vue'
import store from '@/store'
import i18n from '@/i18n'

Vue.use(VueRouter)

// Suppress NavigationDuplicated errors from redundant navigation
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}

const siteName = () => i18n.t('router.siteName')

const routes = [
  {
    path: '/login',
    name: 'login',
    meta: { titleKey: 'router.login' },
    component: () => import('../views/front/Login.vue')
  },
  {
    path: '/',
    component: FrontLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'home',
        meta: { titleKey: 'router.home' },
        component: () => import('../views/front/Home.vue')
      },
      {
        path: 'character/:id',
        name: 'character',
        meta: { titleKey: 'router.character' },
        component: () => import('../views/front/CharacterDetail.vue')
      },
      {
        path: 'note/:id',
        name: 'note',
        meta: { titleKey: 'router.note' },
        component: () => import('../views/front/NoteDetail.vue')
      },
      {
        path: 'publish',
        name: 'publish',
        meta: { titleKey: 'router.publish' },
        component: () => import('../views/front/PublishNote.vue')
      },
      {
        path: 'topics',
        name: 'topics',
        meta: { titleKey: 'router.topics' },
        component: () => import('../views/front/Topics.vue')
      },
      {
        path: 'topic/:id',
        name: 'topic',
        meta: { titleKey: 'router.topic' },
        component: () => import('../views/front/TopicDetail.vue')
      },
      {
        path: 'search',
        name: 'search',
        meta: { titleKey: 'router.search' },
        component: () => import('../views/front/Search.vue')
      },
      {
        path: 'profile',
        name: 'profile',
        meta: { titleKey: 'router.profile' },
        component: () => import('../views/front/Profile.vue')
      },
      {
        path: 'profile/:id',
        name: 'profile-detail',
        meta: { titleKey: 'router.profile' },
        component: () => import('../views/front/Profile.vue')
      },
      {
        path: 'wallet',
        name: 'wallet',
        meta: { titleKey: 'router.wallet' },
        component: () => import('../views/front/Wallet.vue')
      },
      {
        path: 'words',
        name: 'words',
        meta: { titleKey: 'router.words' },
        component: () => import('../views/front/WordBook.vue')
      }
    ]
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    meta: { titleKey: 'router.adminLogin' },
    component: () => import('../views/admin/Login.vue')
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
    children: [
      {
        path: '',
        name: 'dashboard',
        meta: { titleKey: 'router.dashboard', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/Dashboard.vue')
      },
      {
        path: 'users',
        name: 'users',
        meta: { titleKey: 'router.users', roles: ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/Users.vue')
      },
      {
        path: 'roles',
        name: 'roles',
        meta: { titleKey: 'router.roles', roles: ['ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/Roles.vue')
      },
      {
        path: 'permissions',
        name: 'permissions',
        meta: { titleKey: 'router.permissions', roles: ['ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/Permissions.vue')
      },
      {
        path: 'texts',
        name: 'texts',
        meta: { titleKey: 'router.texts', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/TextManagement.vue')
      },
      {
        path: 'settings',
        name: 'settings',
        meta: { titleKey: 'router.settings', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
        component: () => import('../views/admin/Settings.vue')
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  document.title = to.meta.titleKey
    ? `${i18n.t(to.meta.titleKey)} — ${siteName()}`
    : siteName()

  if (requiresAuth && !token) {
    const isAdminRoute = to.path.startsWith('/admin')
    next(isAdminRoute ? '/admin/login' : '/login')
  } else if (token && to.name === 'login') {
    next('/')
  } else if (token && to.name === 'admin-login') {
    const userRoles = store.state.roles
    if (userRoles.some(r => r === 'ROLE_ADMIN' || r === 'ROLE_SUPER_ADMIN' || r === 'ROLE_MODERATOR')) {
      next('/admin')
    } else {
      next()
    }
  } else if (token && requiresAuth) {
    const requiredRoles = to.matched.some(record => record.meta.roles)
      ? to.matched.flatMap(record => record.meta.roles || [])
      : null
    if (requiredRoles) {
      const userRoles = store.state.roles
      if (!requiredRoles.some(r => userRoles.includes(r))) {
        const isAdminRoute = to.path.startsWith('/admin')
        next(isAdminRoute ? '/admin/login' : '/login')
      } else {
        next()
      }
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
